import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Icon,
  InputAdornment,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { useDebounce } from "../../../../hooks/useDebounce";
import { expiryReportService } from "../../services/expiry-report.service";
import {
  ExpiryReportEntry,
  ExpiryReportFilters,
  ExpiryReportResponse,
  ExpiryReportSummary,
} from "../../types/expiry-report.types";
import { AppTable } from "../../../../components/AppTable";
import { TableColumn, TableToolbarActions } from "../../../../app.interface";

const THEME_COLORS = {
  primary: "#0891B2",
  success: "#10B981", // Updated to match API documentation
  error: "#EF4444", // Updated to match API documentation
  warning: "#F59E0B",
  critical: "#DC2626", // Added critical color for expired items
  background: {
    light: "#F8FAFC",
    card: "#FFFFFF",
  },
  border: {
    light: alpha("#0891B2", 0.1),
    main: alpha("#0891B2", 0.2),
  },
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
  },
};

interface ExpiryReportState {
  batches: ExpiryReportEntry[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: ExpiryReportSummary | null;
}

const ExpiryReport: React.FC = () => {
  const theme = useTheme();

  // State management
  const [state, setState] = useState<ExpiryReportState>({
    batches: [],
    loading: false,
    error: null,
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0,
    summary: null,
  });

  // Filters state (using API documentation recommendations)
  const [filters, setFilters] = useState<ExpiryReportFilters>({
    near_expiry_threshold: 7,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [expiryStatus, setExpiryStatus] = useState<
    "all" | "valid" | "near_expiry" | "expired"
  >("all");
  const [includeExpired, setIncludeExpired] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [sortField, setSortField] = useState("days_until_expiry");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Debounced search (300ms delay as recommended in API documentation)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Load expiry report data
  const loadExpiryReport = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const requestFilters: ExpiryReportFilters = {
        ...filters,
        search: debouncedSearchTerm || undefined,
        expiry_status: expiryStatus === "all" ? undefined : expiryStatus,
        include_expired: includeExpired,
        expiry_date_range:
          startDate && endDate
            ? {
                start_date: startDate.valueOf(),
                end_date: endDate.valueOf(),
              }
            : undefined,
      };

      const response = await expiryReportService.getExpiryReport({
        page: state.page,
        page_size: state.pageSize,
        filters: requestFilters,
        sort_field: sortField,
        sort_order: sortOrder,
      });

      if (response.data.success) {
        const data = response.data.data;
        setState((prev) => ({
          ...prev,
          batches: data.batches,
          total: data.total,
          totalPages: data.total_pages,
          summary: data.summary,
          loading: false,
        }));
      } else {
        throw new Error(
          response.data.message || "Failed to load expiry report"
        );
      }
    } catch (error: any) {
      let errorMessage = "Failed to load expiry report";

      // Handle specific error codes from API documentation
      if (error.response?.data?.error?.code) {
        const errorCode = error.response.data.error.code;
        switch (errorCode) {
          case "UNAUTHORIZED_ACCESS":
            errorMessage = "You don't have permission to view expiry reports";
            break;
          case "INVALID_FILTERS":
            errorMessage =
              "Invalid filter parameters. Please check your search criteria";
            break;
          case "CALCULATION_ERROR":
            errorMessage = "Error calculating expiry data. Please try again";
            break;
          default:
            errorMessage = error.response.data.error.message || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      toast.error(errorMessage);
    }
  }, [
    filters,
    debouncedSearchTerm,
    expiryStatus,
    includeExpired,
    startDate,
    endDate,
    state.page,
    state.pageSize,
    sortField,
    sortOrder,
  ]);

  // Effects
  useEffect(() => {
    loadExpiryReport();
  }, [loadExpiryReport]);

  // Event handlers
  const handleFilterChange = (field: keyof ExpiryReportFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
    setState((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setState((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setState((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(direction);
    setState((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      near_expiry_threshold: 7, // Reset to default recommended threshold
    });
    setSearchTerm("");
    setExpiryStatus("all");
    setIncludeExpired(true);
    setStartDate(null);
    setEndDate(null);
    setState((prev) => ({ ...prev, page: 1 }));
  };

  // Utility function for currency formatting
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Column definitions for AppTable
  const getColumns = (): TableColumn[] => {
    return [
      {
        id: "product_id",
        header: "Item ID",
        field: "product_id",
        type: "text",
        width: "w-32",
        sortable: true,
      },
      {
        id: "item_name",
        header: "Item Name",
        field: "item_name",
        type: "text",
        sortable: true,
        width: "min-w-48",
      },
      {
        id: "batch_number",
        header: "Batch No.",
        field: "batch_number",
        type: "text",
        sortable: true,
        width: "w-32",
        accessor: (row: Record<string, any>) => row.batch_number || "N/A",
      },
      {
        id: "manufacturing_date",
        header: "Manufacturing Date",
        field: "manufacturing_date",
        type: "datetime",
        dateFormat: "custom",
        customDateFormat: "MMM d, yyyy",
        sortable: true,
      },
      {
        id: "quantity_remaining",
        header: "Qty Remaining",
        field: "quantity_remaining",
        type: "text",
        align: "right",
        sortable: true,
        accessor: (row: Record<string, any>) =>
          `${row.quantity_remaining?.toLocaleString() || 0} ${
            row.unit_of_measurement
          }`,
      },
      {
        id: "unit_cost",
        header: "Unit Cost",
        field: "unit_cost",
        type: "money",
        align: "right",
        sortable: true,
      },
      {
        id: "warehouse_location",
        header: "Warehouse",
        field: "warehouse_location",
        type: "text",
        sortable: true,
        accessor: (row: Record<string, any>) => row.warehouse_location || "-",
      },
      {
        id: "expiry_date",
        header: "Expiry Date",
        field: "expiry_date",
        type: "datetime",
        dateFormat: "custom",
        customDateFormat: "MMM d, yyyy",
        sortable: true,
      },
      {
        id: "days_until_expiry",
        header: "Days Until Expiry",
        field: "days_until_expiry",
        type: "text",
        align: "center",
        sortable: true,
        accessor: (row: Record<string, any>) => {
          const days = row.days_until_expiry;

          // Format label according to API documentation recommendations
          if (days < 0) {
            return `${Math.abs(days)} days ago`;
          } else if (days === 0) {
            return "Today";
          } else {
            return `${days} days`;
          }
        },
      },
      {
        id: "total_value",
        header: "Total Value",
        field: "total_value",
        type: "money",
        align: "right",
        sortable: true,
      },
      {
        id: "expiry_status",
        header: "Status",
        field: "expiry_status",
        type: "text",
        align: "center",
        sortable: true,
        accessor: (row: Record<string, any>) => {
          const status = row.expiry_status;
          switch (status) {
            case "valid":
              return "Valid";
            case "near_expiry":
              return "Near Expiry";
            case "expired":
              return "Expired";
            default:
              return status;
          }
        },
      },
    ];
  };

  // Toolbar actions for AppTable
  const toolbarActions: TableToolbarActions = {
    refresh: {
      label: "Refresh Data",
      is_disabled: state.loading,
      onClick: loadExpiryReport,
    },
    export: {
      label: "Export Data",
      is_disabled: state.loading || state.batches.length === 0,
      options: {
        formats: ["csv", "excel"],
        filename: "expiry-report",
      },
      datasource: "expiry_report",
      filters: [
        ...(filters.category
          ? [
              {
                id: "category",
                title: "Category",
                field: "category",
                operator: "equals",
                value: filters.category,
              },
            ]
          : []),
        ...(filters.warehouse_location
          ? [
              {
                id: "warehouse",
                title: "Warehouse",
                field: "warehouse_location",
                operator: "equals",
                value: filters.warehouse_location,
              },
            ]
          : []),
        ...(debouncedSearchTerm
          ? [
              {
                id: "search",
                title: "Search",
                field: "item_name",
                operator: "contains",
                value: debouncedSearchTerm,
              },
            ]
          : []),
        ...(expiryStatus !== "all"
          ? [
              {
                id: "expiry_status",
                title: "Expiry Status",
                field: "expiry_status",
                operator: "equals",
                value: expiryStatus,
              },
            ]
          : []),
      ],
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ color: THEME_COLORS.text.primary }}
          >
            Expiry Report
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: THEME_COLORS.text.secondary }}
          >
            Monitor batch expiry dates and track items nearing expiration or
            already expired
          </Typography>
        </Box>

        {/* Summary Cards */}
        {state.summary && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary.main">
                    Total Batches
                  </Typography>
                  <Typography variant="h5">
                    {state.summary.total_batches.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Batches
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="error.main">
                    Expired
                  </Typography>
                  <Typography variant="h5">
                    {state.summary.status_breakdown.expired.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(
                      state.summary.status_breakdown.expired.value
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="warning.main">
                    Near Expiry
                  </Typography>
                  <Typography variant="h5">
                    {state.summary.status_breakdown.near_expiry.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(
                      state.summary.status_breakdown.near_expiry.value
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="success.main">
                    Total Value
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(state.summary.total_value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All Batches
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            {/* Search */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Item name, batch number..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category || ""}
                  label="Category"
                  onChange={(e) =>
                    handleFilterChange("category", e.target.value)
                  }
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="ingredients">Ingredients</MenuItem>
                  <MenuItem value="beverages">Beverages</MenuItem>
                  <MenuItem value="packaging">Packaging</MenuItem>
                  <MenuItem value="supplies">Supplies</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Warehouse */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Warehouse</InputLabel>
                <Select
                  value={filters.warehouse_location || ""}
                  label="Warehouse"
                  onChange={(e) =>
                    handleFilterChange("warehouse_location", e.target.value)
                  }
                >
                  <MenuItem value="">All Warehouses</MenuItem>
                  <MenuItem value="Main Storage">Main Storage</MenuItem>
                  <MenuItem value="Secondary Storage">
                    Secondary Storage
                  </MenuItem>
                  <MenuItem value="Main Warehouse">Main Warehouse</MenuItem>
                  <MenuItem value="Cold Storage">Cold Storage</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Expiry Status */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={expiryStatus}
                  label="Status"
                  onChange={(e) =>
                    setExpiryStatus(
                      e.target.value as
                        | "all"
                        | "valid"
                        | "near_expiry"
                        | "expired"
                    )
                  }
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="valid">Valid</MenuItem>
                  <MenuItem value="near_expiry">Near Expiry</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearFilters}
                sx={{ height: "56px" }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>

          {/* Date Range Filters */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Expiry From"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue as Dayjs | null)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Expiry To"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue as Dayjs | null)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeExpired}
                    onChange={(e) => setIncludeExpired(e.target.checked)}
                  />
                }
                label="Include already expired items"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Error Display */}
        {state.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {state.error}
          </Alert>
        )}

        {/* AppTable */}
        <AppTable
          data={state.batches.map((batch) => ({
            ...batch,
            _id: batch.batch_id,
          }))}
          columns={getColumns()}
          loading={state.loading}
          sortConfig={{
            field: sortField,
            direction: sortOrder,
          }}
          onSort={handleSort}
          pagination={{
            total: state.total,
            page: state.page,
            pageSize: state.pageSize,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          toolbarActions={toolbarActions}
          styleOptions={{
            bordered: true,
            columnSeparators: false,
          }}
          emptyState={{
            icon: "schedule",
            title: "No Expiry Data Found",
            description:
              "There are no batches matching your current filters. Try adjusting your search criteria.",
            actionLabel: "Clear Filters",
            onAction: clearFilters,
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default ExpiryReport;
